// lib/list/factoryProfile.ts
import type { Workspace } from '#types/types';

export type FactoryImage = {
  id: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  createdAt: number;
};

export type FactoryFolder = {
  id: string;
  name: string;
  instructions: string;
  imagesLocked?: boolean;
  /** Immutable system folders are identified by this marker, never by their display name. */
  systemPreset?: { family: string; version: number };
  images: FactoryImage[];
};

export type FactoryDerivedDirection = {
  prompt: string;
  fingerprint: string;
  analysisId: string;
  analyzedAt: number;
  /** The current eligible bank and its bounded native subset, for inspection. */
  referenceCount: number;
  nativeReferenceCount: number;
  nativeReferenceIds: string[];
  nativeReferences: Array<{ id: string; url: string }>;
};

export type ListFactoryProfile = {
  version: 4;
  folders: FactoryFolder[];
  /** Explicit, durable constraints authored by the user. */
  userInstructions: string;
  /** The previous learned prompt is inspectable only; each Factory run derives a new one. */
  lastDerivedDirection?: FactoryDerivedDirection;
  /** @deprecated Legacy display/cache field retained for profile compatibility. */
  masterPrompt: string;
  updatedAt: number;
  diversityCursor?: number;
  /** A project-level removal remains effective when a preset's manifest is upgraded. */
  presetOptOuts?: Record<string, { version: number; removedAt: number }>;
  /** Derived for generation compatibility; it is never persisted separately. */
  referenceUrls: string[];
};

const MAX_REFERENCES = 40;
export const MAX_FACTORY_PROMPT_LENGTH = 4000;
export const FACTORY_USER_DIRECTION_HEADING = '[User direction]';
const LEGACY_FOLDER_NAME = 'Reference images';
export const FACTORY_VALIDATED_OUTPUTS_FOLDER_ID = 'factory-validated-outputs';
export const FACTORY_VALIDATED_OUTPUTS_FOLDER_NAME = 'Validated outputs';

const cleanText = (value: unknown, limit: number) => typeof value === 'string' ? value.trim().slice(0, limit) : '';
const makeId = (prefix: string, index: number) => `${prefix}-${index}`;
const cleanDiversityCursor = (value: unknown): number => (
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0
);
const readPresetOptOuts = (value: unknown): Record<string, { version: number; removedAt: number }> => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, { version: number; removedAt: number }>>((result, [family, entry]) => {
    if (!/^[a-z0-9-]{1,120}$/i.test(family) || !entry || typeof entry !== 'object') return result;
    const record = entry as Record<string, unknown>;
    if (typeof record.version !== 'number' || !Number.isInteger(record.version) || record.version < 1) return result;
    if (typeof record.removedAt !== 'number' || !Number.isFinite(record.removedAt) || record.removedAt < 0) return result;
    result[family] = { version: record.version, removedAt: record.removedAt };
    return result;
  }, {});
};
const readSystemPreset = (value: unknown): FactoryFolder['systemPreset'] => {
  if (!value || typeof value !== 'object') return undefined;
  const record = value as Record<string, unknown>;
  const family = cleanText(record.family, 120);
  const version = record.version;
  return family && /^[a-z0-9-]+$/i.test(family) && typeof version === 'number' && Number.isInteger(version) && version > 0
    ? { family, version }
    : undefined;
};
const readDerivedDirection = (value: unknown): FactoryDerivedDirection | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const direction = value as Record<string, unknown>;
  const prompt = cleanText(direction.prompt, MAX_FACTORY_PROMPT_LENGTH);
  const fingerprint = cleanText(direction.fingerprint, 128);
  const analysisId = cleanText(direction.analysisId, 128);
  const analyzedAt = typeof direction.analyzedAt === 'number' && Number.isFinite(direction.analyzedAt)
    ? direction.analyzedAt
    : 0;
  const referenceCount = typeof direction.referenceCount === 'number' && Number.isInteger(direction.referenceCount) && direction.referenceCount >= 0
    ? direction.referenceCount
    : 0;
  const nativeReferenceCount = typeof direction.nativeReferenceCount === 'number' && Number.isInteger(direction.nativeReferenceCount) && direction.nativeReferenceCount >= 0
    ? direction.nativeReferenceCount
    : 0;
  const nativeReferenceIds = Array.isArray(direction.nativeReferenceIds)
    ? direction.nativeReferenceIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0).slice(0, 14)
    : [];
  const nativeReferences = Array.isArray(direction.nativeReferences)
    ? direction.nativeReferences.flatMap((reference) => {
      if (!reference || typeof reference !== 'object') return [];
      const value = reference as Record<string, unknown>;
      const id = cleanText(value.id, 120);
      const url = cleanText(value.url, 4000);
      return id && isHttpsUrl(url) ? [{ id, url }] : [];
    }).slice(0, 14)
    : [];
  return prompt && fingerprint && analysisId && analyzedAt > 0
    ? { prompt, fingerprint, analysisId, analyzedAt, referenceCount, nativeReferenceCount, nativeReferenceIds, nativeReferences }
    : undefined;
};

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const isFactoryAssetUrl = (value: string): boolean => (
  value.startsWith('/api/assets/')
  || value.startsWith('/api/user-files/')
  || isHttpsUrl(value)
);

/**
 * The generated Factory folder stays out of persisted camera state: it is a
 * live project projection, not a user-owned reference bank. It is a review
 * surface only: derived outputs never feed back into the Factory reference
 * bank.
 */
type ValidatedProjectLayer = {
  id: string;
  name?: string | null;
  imageSrc?: string | null;
  videoFramesUrls?: readonly string[] | null;
  timestamp?: number | string | null;
};

const normalizeOutputTimestamp = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

export const createValidatedOutputsFolder = (
  workspaces: Iterable<Pick<Workspace, 'versions'>>,
  approvedVersionIds: ReadonlySet<string> = new Set(),
  durableApprovedOutputs: readonly FactoryImage[] = [],
  rejectedVersionIds: ReadonlySet<string> = new Set(),
  projectLayers: Iterable<ValidatedProjectLayer> = [],
): FactoryFolder => {
  const workspaceCandidates: FactoryImage[] = [];
  const projectLayerCandidates: FactoryImage[] = [];
  for (const layer of projectLayers) {
    const id = typeof layer.id === 'string' ? layer.id.trim() : '';
    if (!id || rejectedVersionIds.has(id) || !approvedVersionIds.has(id)) continue;
    const url = typeof layer.imageSrc === 'string' && layer.imageSrc.trim()
      ? layer.imageSrc.trim()
      : Array.isArray(layer.videoFramesUrls)
        ? layer.videoFramesUrls.find((candidate) => typeof candidate === 'string' && candidate.trim())?.trim()
        : undefined;
    if (!url || !isFactoryAssetUrl(url)) continue;
    projectLayerCandidates.push({
      id,
      name: typeof layer.name === 'string' && layer.name.trim() ? layer.name.trim() : 'Validated output',
      url,
      createdAt: normalizeOutputTimestamp(layer.timestamp),
    });
  }
  for (const workspace of workspaces) {
    for (const version of workspace.versions ?? []) {
      const url = typeof version?.imageUrl === 'string' ? version.imageUrl.trim() : '';
      if (
        rejectedVersionIds.has(version?.id)
        || (version?.status !== 'VALIDATED' && !approvedVersionIds.has(version?.id))
        || !url
        || !isFactoryAssetUrl(url)
      ) continue;
      workspaceCandidates.push({
        id: version.id,
        name: version.name?.trim() || 'Validated output',
        url,
        createdAt: typeof version.timestamp === 'number' ? version.timestamp : 0,
      });
    }
  }
  const durableCandidates: FactoryImage[] = [];
  for (const output of durableApprovedOutputs) {
    const url = output.url.trim();
    if (!approvedVersionIds.has(output.id) || rejectedVersionIds.has(output.id) || !isFactoryAssetUrl(url)) continue;
    durableCandidates.push({ ...output, url });
  }

  const seenIds = new Set<string>();
  const seenUrls = new Set<string>();
  const preferNewest = (left: FactoryImage, right: FactoryImage) => (
    right.createdAt - left.createdAt || left.id.localeCompare(right.id)
  );
  // Resolve duplicates inside the hydrated workspace first; durable snapshot media is a fallback
  // for versions that the current workspace projection has not loaded.
  const images = [...workspaceCandidates.sort(preferNewest), ...projectLayerCandidates.sort(preferNewest), ...durableCandidates.sort(preferNewest)]
    .filter((image) => {
      if (seenIds.has(image.id) || seenUrls.has(image.url)) return false;
      seenIds.add(image.id);
      seenUrls.add(image.url);
      return true;
    })
    .sort(preferNewest);

  return {
    id: FACTORY_VALIDATED_OUTPUTS_FOLDER_ID,
    name: FACTORY_VALIDATED_OUTPUTS_FOLDER_NAME,
    instructions: '',
    imagesLocked: true,
    images,
  };
};

/** The system folder is always last, leaving user-managed references first. */
export const composeFactoryFolders = (
  folders: readonly FactoryFolder[],
  validatedOutputsFolder?: FactoryFolder | null,
): FactoryFolder[] => [
  ...folders.filter((folder) => folder.id !== FACTORY_VALIDATED_OUTPUTS_FOLDER_ID),
  ...(validatedOutputsFolder ? [validatedOutputsFolder] : []),
];

export type FactoryReferenceImage = FactoryImage & { folderId: string };

export const factoryReferenceImagesForFolders = (folders: readonly FactoryFolder[]): FactoryReferenceImage[] => {
  const seen = new Set<string>();
  const ids = new Set<string>();
  const images: FactoryReferenceImage[] = [];
  // Immutable preset folders must survive the 40-image transport cap; custom
  // folders still follow in their saved order with the remaining capacity.
  const orderedFolders = [...folders].sort((left, right) => Number(Boolean(right.systemPreset)) - Number(Boolean(left.systemPreset)));
  for (const folder of orderedFolders) {
    // `imagesLocked` controls editing in the panel. A curated read-only folder is
    // still a valid direction source; only Factory's generated output folder must
    // never be fed back into a future Factory run.
    if (folder.id === FACTORY_VALIDATED_OUTPUTS_FOLDER_ID) continue;
    for (const image of folder.images) {
      const url = image.url.trim();
      if (!isHttpsUrl(url) || seen.has(url) || ids.has(image.id)) continue;
      seen.add(url);
      ids.add(image.id);
      images.push({ ...image, url, folderId: folder.id });
      if (images.length === MAX_REFERENCES) return images;
    }
  }
  return images;
};

export const factoryReferenceUrlsForFolders = (folders: readonly FactoryFolder[]): string[] =>
  factoryReferenceImagesForFolders(folders).map((image) => image.url);

export const factoryReferenceUrls = (profile: Pick<ListFactoryProfile, 'folders'>): string[] =>
  factoryReferenceUrlsForFolders(profile.folders);

export const createEmptyListFactoryProfile = (): ListFactoryProfile => ({
  version: 4,
  folders: [],
  userInstructions: '',
  masterPrompt: '',
  updatedAt: 0,
  diversityCursor: 0,
  presetOptOuts: {},
  referenceUrls: [],
});

const readFolders = (raw: Record<string, unknown>): FactoryFolder[] => {
  if (!Array.isArray(raw.folders)) return [];
  return raw.folders.map((folder, folderIndex) => {
    const value = folder && typeof folder === 'object' ? folder as Record<string, unknown> : {};
    const images = Array.isArray(value.images) ? value.images : [];
    return {
      id: cleanText(value.id, 120) || makeId('folder', folderIndex),
      name: cleanText(value.name, 120) || `Folder ${folderIndex + 1}`,
      instructions: cleanText(value.instructions, 4000),
      imagesLocked: value.imagesLocked === true,
      systemPreset: readSystemPreset(value.systemPreset),
      images: images.map((image, imageIndex) => {
        const item = image && typeof image === 'object' ? image as Record<string, unknown> : {};
        return {
          id: cleanText(item.id, 120) || makeId(`image-${folderIndex}`, imageIndex),
          name: cleanText(item.name, 240) || `Reference ${imageIndex + 1}`,
          url: cleanText(item.url, 4000),
          thumbnailUrl: cleanText(item.thumbnailUrl, 4000) || undefined,
          createdAt: typeof item.createdAt === 'number' ? item.createdAt : 0,
        };
      }).filter((image) => image.url),
    };
  });
};

export const readListFactoryProfile = (cameraState: unknown): ListFactoryProfile | null => {
  if (!cameraState || typeof cameraState !== 'object') return null;
  const raw = (cameraState as { factory?: unknown }).factory;
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const folders = readFolders(value);
  const legacyUrls = Array.isArray(value.referenceUrls)
    ? value.referenceUrls.filter((url): url is string => typeof url === 'string' && url.trim().length > 0).slice(0, MAX_REFERENCES)
    : [];
  const migratedFolders = folders.length || !legacyUrls.length ? folders : [{
    id: 'legacy-references',
    name: LEGACY_FOLDER_NAME,
    instructions: '',
    images: legacyUrls.map((url, index) => ({ id: `legacy-image-${index}`, name: `Reference ${index + 1}`, url, createdAt: 0 })),
  }];
  const legacyMasterPrompt = cleanText(value.masterPrompt, MAX_FACTORY_PROMPT_LENGTH);
  const profile: ListFactoryProfile = {
    ...createEmptyListFactoryProfile(),
    folders: migratedFolders,
    userInstructions: cleanText(value.userInstructions, MAX_FACTORY_PROMPT_LENGTH),
    lastDerivedDirection: readDerivedDirection(value.lastDerivedDirection),
    masterPrompt: legacyMasterPrompt,
    updatedAt: typeof value.updatedAt === 'number' ? value.updatedAt : 0,
    diversityCursor: cleanDiversityCursor(value.diversityCursor),
    presetOptOuts: readPresetOptOuts(value.presetOptOuts),
    referenceUrls: [],
  };
  return { ...profile, referenceUrls: factoryReferenceUrls(profile) };
};

export const writeListFactoryProfile = (cameraState: unknown, profile: ListFactoryProfile | null): Record<string, unknown> => {
  const state = cameraState && typeof cameraState === 'object' ? cameraState as Record<string, unknown> : {};
  if (!profile) return { ...state, factory: null };
  const folders = profile.folders
    .filter((folder) => folder.id !== FACTORY_VALIDATED_OUTPUTS_FOLDER_ID)
    .map((folder) => ({ ...folder, images: folder.images.slice(0, MAX_REFERENCES) }));
  return {
    ...state,
    factory: {
      version: 4,
      folders,
      userInstructions: cleanText(profile.userInstructions, MAX_FACTORY_PROMPT_LENGTH),
      lastDerivedDirection: profile.lastDerivedDirection
        ? {
          prompt: profile.lastDerivedDirection.prompt.trim().slice(0, MAX_FACTORY_PROMPT_LENGTH),
          fingerprint: profile.lastDerivedDirection.fingerprint.trim().slice(0, 128),
          analysisId: profile.lastDerivedDirection.analysisId.trim().slice(0, 128),
          analyzedAt: profile.lastDerivedDirection.analyzedAt,
          referenceCount: profile.lastDerivedDirection.referenceCount,
          nativeReferenceCount: profile.lastDerivedDirection.nativeReferenceCount,
          nativeReferenceIds: profile.lastDerivedDirection.nativeReferenceIds.slice(0, 14),
          nativeReferences: profile.lastDerivedDirection.nativeReferences.slice(0, 14),
        }
        : undefined,
      masterPrompt: cleanText(profile.masterPrompt, MAX_FACTORY_PROMPT_LENGTH),
      updatedAt: profile.updatedAt,
      diversityCursor: cleanDiversityCursor(profile.diversityCursor),
      presetOptOuts: readPresetOptOuts(profile.presetOptOuts),
    },
  };
};

export const factoryPromptFor = (direction: string, instruction: string): string => {
  const trimmedInstruction = instruction.trim();
  const trimmedPrompt = direction.trim();
  if (!trimmedPrompt) return trimmedInstruction;
  return `[Factory style card]\n${trimmedPrompt}\n\n[Colour-grade fidelity]\nTreat the style card's colour grade as a reference-bank constraint: do not intensify saturation, contrast, hue separation, or grading beyond that baseline. Explicit colour, palette, saturation, or grading requirements in [Brief] override this constraint.\n\n[Brief]\n${trimmedInstruction}\n\n[Originality]\nUse the supplied reference bank only for shared visual language. Preserve every subject, setting, action, pose, framing, and product named in the brief. For choices the brief leaves open, create a novel treatment without recreating any source image.`;
};

const FACTORY_USER_DIRECTION_BOUNDARY = /(?:^|\r?\n\r?\n)\[User direction\]\r?\n/i;

/** Remove the one system-managed user-direction tail while preserving every authored base section. */
export const stripFactoryUserDirection = (masterPrompt: string): string => {
  const match = FACTORY_USER_DIRECTION_BOUNDARY.exec(masterPrompt);
  return (match ? masterPrompt.slice(0, match.index) : masterPrompt).trim();
};

/** Keep user text from creating a second system-managed section boundary. */
export const normalizeFactoryUserDirection = (instruction: string): string => instruction
  .replace(/\[\s*user direction\s*\]/gi, 'User direction')
  .trim();

/**
 * Compose the inspectable prompt exactly as Factory will reserve it. Returning null is an atomic
 * validation failure: neither the derived card nor the user direction may be silently truncated.
 */
export const composeFactoryMasterPrompt = (basePrompt: string, instruction: string): string | null => {
  const base = stripFactoryUserDirection(basePrompt);
  const userDirection = normalizeFactoryUserDirection(instruction);
  const prompt = userDirection
    ? `${base}${base ? '\n\n' : ''}${FACTORY_USER_DIRECTION_HEADING}\n${userDirection}`
    : base;
  return prompt.length <= MAX_FACTORY_PROMPT_LENGTH ? prompt : null;
};
